import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { BookOpen, Loader2, ChevronLeft, Play, CheckCircle2 } from "lucide-react";

export default function MyCourses() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/my-courses");
      setEnrollments(data.enrollments || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <DashboardLayout>
      <div data-testid="my-courses">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">وحدة التعلّم</div>
          <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">برنامج الإعداد والتهيئة</h1>
          <p className="mt-2 text-slate-600">تابع تقدّمك في المقررات التمهيدية للتدريب التعاوني.</p>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="my-courses-error" />
        ) : enrollments.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title="لا يوجد محتوى تهيئة نشط"
              description="يتم تسجيلك تلقائياً في برنامج الإعداد والتهيئة عند قبول طلبك على فرصة تدريب تعاوني مرتبطة بمحتوى."
              testid="my-courses-empty"
            action={
              <Link to="/dashboard/browse-opportunities" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 transition-colors">
                استكشف الفرص <ChevronLeft className="h-4 w-4" />
              </Link>
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {enrollments.map((e) => {
              const c = e.course || {};
              const done = e.progress_percent >= 100;
              return (
                <Link key={e.id} to={`/dashboard/courses/${e.course_id}/learn`} className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:card-shadow-lg hover:border-teal-200 transition-all" data-testid={`enrollment-${e.id}`}>
                  <div className="h-40 bg-slate-100 relative overflow-hidden">
                    {c.cover_image_url ? (
                      <img src={c.cover_image_url} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-teal-700 to-teal-900 grid place-items-center">
                        <BookOpen className="h-14 w-14 text-white/60" strokeWidth={1.5} />
                      </div>
                    )}
                    {done && (
                      <div className="absolute top-3 start-3 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> مكتمل
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-bold text-lg text-slate-900 line-clamp-1">{c.title || "مقرر"}</h3>
                    {c.program_name && <div className="text-xs text-teal-700 font-medium mt-1">{c.program_name}</div>}
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs text-slate-600 mb-1.5">
                        <span>التقدّم</span>
                        <span className="font-bold text-slate-900 arabic-numerals">{Math.round(e.progress_percent || 0)}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-700 rounded-full transition-all" style={{ width: `${e.progress_percent || 0}%` }} />
                      </div>
                    </div>
                    <div className="mt-5 inline-flex items-center gap-1.5 text-teal-700 font-semibold text-sm group-hover:gap-2 transition-all">
                      <Play className="h-4 w-4" /> {done ? "مراجعة" : "استكمال التعلّم"}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
